import axios from 'axios'
import _isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { memo, useCallback, useEffect } from 'react'
import { P, match } from 'ts-pattern'

import { SupportedMarketKey } from '@nxt-insurance/constants'

import { useAnalytics } from '@/context/AnalyticsContext'
import { reportError } from '@/services/errorReporting'
import { processCookies } from '@/utils/cookies'
import { getLocaleParts } from '@/utils/locale'
import type { UserConsent } from '@helpers/cookies/types'
import { COOKIES_CATEGORIES, getAllowedCookies } from '@helpers/cookies/utils'

const marketHideRejectButton: Record<SupportedMarketKey, boolean> = {
  [SupportedMarketKey.De]: true,
  [SupportedMarketKey.At]: false,
}

// For testing this locally it is necessary to update staging url in the
// CookieFirst panel and update url for cookieFirstSrc prop
export const ConsentManager = memo(() => {
  const { locale, query } = useRouter()
  const [_, market] = getLocaleParts(locale)
  const { conditionallyLoadSegmentAnalytics, appendQueryParametersToAnchorTags } = useAnalytics()
  processCookies(query, COOKIES_CATEGORIES.necessary_cookies)

  const handleUserConsent = useCallback(
    async (consentEventDetails?: UserConsent) => {
      const hasConsented = window.CookieFirst?.hasConsented ?? false
      if (hasConsented || consentEventDetails) {
        try {
          const currentConsent = window.CookieFirst?.acceptedServices
          match({
            currentConsent,
            newConsent: consentEventDetails,
          })
            // when consent given in cookieFirst update event is different from current one
            .with(
              P.when((data) => !_isEqual(data.currentConsent, data.newConsent)),
              (data) => {
                // consent changed
                // @ts-expect-error one of the entries is not undefined for sure
                conditionallyLoadSegmentAnalytics(data.newConsent ?? data.currentConsent).catch(
                  reportError
                )
              }
            )
            .otherwise(() => {
              appendQueryParametersToAnchorTags()
            })
          const allowedCookies = getAllowedCookies(consentEventDetails ?? currentConsent ?? {})
          processCookies(query, allowedCookies)
        } catch (error) {
          if (axios.isAxiosError(error) || error instanceof Error) {
            reportError(error)
          }
        }
      }
    },
    [query, appendQueryParametersToAnchorTags, conditionallyLoadSegmentAnalytics]
  )

  // Necessary cookies should be stored even if consent was not given
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('cf_init', () => {
        handleUserConsent().catch(reportError)
      })
      window.addEventListener('cf_services_consent', (event: Event & { detail?: UserConsent }) => {
        handleUserConsent(event.detail).catch(reportError)
      })
      return () => {
        window.removeEventListener('cf_init', () => {
          handleUserConsent().catch(reportError)
        })
        window.removeEventListener('cf_services_consent', () => {
          handleUserConsent().catch(reportError)
        })
      }
    }
  }, [handleUserConsent])

  if (!marketHideRejectButton[market]) {
    return null
  }

  return null
})
